import { useState, useRef } from "react";
import { useApi } from "src/hooks/useApi";
import { apiRequest } from "src/services/apiClient";
import { useTranslation } from "react-i18next";
import { TbCheck, TbMessageCircle, TbSend, TbPaperclip, TbDownload } from "react-icons/tb";
import { DetailCard } from "src/components/Details/Details";
import { config } from "configs/config";
import "src/pages/RecipientPage/recipientPage.scss";

async function downloadAttachment(path: string, filename: string) {
    const token    = localStorage.getItem("token");
    const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
    const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;
    const res = await fetch(`${config.baseUrl}${path}`, {
        headers: {
            ...(token    && { Authorization: `Bearer ${token}` }),
            ...(doctorId && { "X-Doctor-Id": doctorId }),
            ...(praxisId && { "X-Praxis-Id": praxisId }),
        },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

interface ReplyAttachment {
    id:       string;
    name:     string;
    mimetype: string;
}

interface QuestionReply {
    id:          string;
    text:        string;
    doctorName:  string;
    repliedAt:   string;
    attachments: ReplyAttachment[];
}

interface InvoiceQuestion {
    id:          string;
    category:    string;
    text:        string;
    submittedAt: string;
    resolved:    boolean;
    resolvedAt?: string;
    resolvedBy?: string;
    replies:     QuestionReply[];
}

const CATEGORY_COLORS: Record<string, string> = {
    general:     "#6366f1",
    billing:     "#f59e0b",
    appointment: "#22c55e",
    treatment:   "#3b82f6",
    insurance:   "#ec4899",
};

function CategoryBadge({ category }: { category: string }) {
    const { t } = useTranslation();
    const color = CATEGORY_COLORS[category] ?? "#6b7280";
    return (
        <span className="q-badge" style={{ background: color + "18", color }}>
            {t(`recipientPage.category_${category}`, { defaultValue: category })}
        </span>
    );
}

function ReplyForm({
    invoiceId,
    questionId,
    onSent,
}: {
    invoiceId:  string;
    questionId: string;
    onSent:     (reply: QuestionReply) => void;
}) {
    const { t } = useTranslation();
    const [text, setText] = useState("");
    const [files, setFiles] = useState<File[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    const handleSubmit = async () => {
        if (!text.trim() || submitting) return;
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("text", text.trim());
            files.forEach((f) => fd.append("files", f));

            const reply = await apiRequest<QuestionReply>(
                `/invoices/${invoiceId}/questions/${questionId}/reply`,
                { method: "POST", body: fd },
                {},
            );
            onSent(reply);
            setText("");
            setFiles([]);
        } catch {
            // ignore
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="q-reply-form">
            <textarea
                className="q-reply-form__textarea"
                placeholder={t("invoicePage.replyPlaceholder")}
                value={text}
                onChange={(e) => setText(e.target.value)}
            />
            <div className="q-reply-form__actions">
                <label className="q-reply-form__file-label">
                    <TbPaperclip />
                    {t("invoicePage.attachFiles")}
                    <input
                        ref={fileRef}
                        type="file"
                        multiple
                        onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
                    />
                </label>
                {files.length > 0 && (
                    <span className="q-reply-form__file-list">
                        {files.map((f) => f.name).join(", ")}
                    </span>
                )}
                <button
                    className="q-reply-form__submit"
                    onClick={handleSubmit}
                    disabled={!text.trim() || submitting}
                >
                    <TbSend />
                    {submitting ? t("invoicePage.replySending") : t("invoicePage.replySend")}
                </button>
            </div>
        </div>
    );
}

function QuestionCard({
    question,
    invoiceId,
    onResolve,
    resolving,
    onReplySent,
}: {
    question:    InvoiceQuestion;
    invoiceId:   string;
    onResolve:   (id: string) => void;
    resolving:   string | null;
    onReplySent: () => void;
}) {
    const { t } = useTranslation();
    const [showReply, setShowReply] = useState(false);
    const [replies, setReplies] = useState<QuestionReply[]>(question.replies ?? []);

    const attachmentPath = (questionId: string, replyId: string, attachmentId: string) =>
        `/api/invoices/${invoiceId}/questions/${questionId}/replies/${replyId}/attachments/${attachmentId}`;

    return (
        <div className={`q-card${question.resolved ? " q-card--resolved" : ""}`}>
            <div className="q-card__header">
                <CategoryBadge category={question.category} />
                <span className="q-card__date">
                    {new Date(question.submittedAt).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" })}
                    {" "}
                    {new Date(question.submittedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                </span>
                {question.resolved ? (
                    <span className="q-card__resolved-chip">
                        <TbCheck /> {t("recipientPage.questionnaire_resolved")}
                    </span>
                ) : (
                    <button
                        className="q-card__resolve-btn"
                        onClick={() => onResolve(question.id)}
                        disabled={resolving === question.id}
                    >
                        <TbCheck /> {resolving === question.id ? t("recipientPage.saving") : t("recipientPage.questionnaire_resolve")}
                    </button>
                )}
            </div>

            <p className="q-card__text">{question.text}</p>

            {replies.length > 0 && (
                <div className="q-replies">
                    {replies.map((r) => (
                        <div key={r.id} className="q-reply">
                            <div className="q-reply__header">
                                <span className="q-reply__author">{r.doctorName}</span>
                                <span className="q-reply__date">
                                    {new Date(r.repliedAt).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" })}
                                    {" "}
                                    {new Date(r.repliedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                                </span>
                            </div>
                            <p className="q-reply__text">{r.text}</p>
                            {r.attachments.length > 0 && (
                                <div className="q-reply__attachments">
                                    {r.attachments.map((a) => (
                                        <button
                                            key={a.id}
                                            className="q-reply__attachment"
                                            onClick={() => downloadAttachment(attachmentPath(question.id, r.id, a.id), a.name)}
                                        >
                                            <TbDownload size={12} />
                                            {a.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {!question.resolved && (
                <>
                    <button
                        className="q-reply-toggle"
                        onClick={() => setShowReply((v) => !v)}
                    >
                        <TbMessageCircle size={13} />
                        {showReply ? t("invoicePage.replyHide") : t("invoicePage.replyOpen")}
                    </button>
                    {showReply && (
                        <ReplyForm
                            invoiceId={invoiceId}
                            questionId={question.id}
                            onSent={(reply) => {
                                setReplies((prev) => [...prev, reply]);
                                setShowReply(false);
                                onReplySent();
                            }}
                        />
                    )}
                </>
            )}
        </div>
    );
}

export function InvoiceQuestionsTab({ invoiceId }: { invoiceId: string }) {
    const { t } = useTranslation();
    const { data: questions, loading, refetch } = useApi<InvoiceQuestion[]>(
        `/invoices/${invoiceId}/questions`,
    );
    const [resolving, setResolving] = useState<string | null>(null);

    const handleResolve = async (questionId: string) => {
        setResolving(questionId);
        try {
            await apiRequest(`/invoices/${invoiceId}/questions/${questionId}/resolve`, { method: "PATCH" });
            refetch();
        } catch {
            // ignore
        } finally {
            setResolving(null);
        }
    };

    if (loading) {
        return (
            <DetailCard title={t("invoicePage.tab_questions")} full>
                <div className="q-tab">
                    {[1, 2].map((i) => (
                        <div key={i} className="q-card q-card--skeleton">
                            <span className="skeleton" style={{ width: 60, height: 20, borderRadius: 12 }} />
                            <span className="skeleton" style={{ width: "70%", height: 14, marginTop: 8 }} />
                        </div>
                    ))}
                </div>
            </DetailCard>
        );
    }

    const list = questions ?? [];
    const open = list.filter((q) => !q.resolved).length;

    return (
        <DetailCard
            title={
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <TbMessageCircle />
                    {t("invoicePage.tab_questions")}
                    {list.length > 0 && (
                        <span className="q-count">
                            {open > 0
                                ? t("recipientPage.questionnaire_openCount", { count: open })
                                : t("recipientPage.questionnaire_allResolved")}
                        </span>
                    )}
                </span>
            }
            full
        >
            {list.length === 0 ? (
                <p className="invoice-log__empty">{t("invoicePage.noQuestions")}</p>
            ) : (
                <div className="q-tab">
                    <div className="q-list">
                        {list.map((q) => (
                            <QuestionCard
                                key={q.id}
                                question={q}
                                invoiceId={invoiceId}
                                onResolve={handleResolve}
                                resolving={resolving}
                                onReplySent={refetch}
                            />
                        ))}
                    </div>
                </div>
            )}
        </DetailCard>
    );
}
